import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ImmutableData, TableRowDrop, WaxService} from './wax-service.service';
import {from, interval, Observable} from 'rxjs';
import {flatMap, map, startWith} from 'rxjs/operators';
import {CountdownConfig, CountdownEvent} from 'ngx-countdown';
import {MatSnackBar} from '@angular/material/snack-bar';

const currencyContracts = {
  DUST: 'niftywizards',
  WAX: 'eosio.token'
};
const currencyPrecisions = {
  DUST: 4,
  WAX: 8
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  title = 'autoclaim';
  waxPrice$: Observable<number>;
  drops$: Observable<TableRowDrop[]>;
  liveDrops$: Observable<TableRowDrop[]>;
  upcomingDrops$: Observable<TableRowDrop[]>;
  assets$: Record<string, Observable<ImmutableData>> = {};
  constructor(public waxService: WaxService, public snackBar: MatSnackBar) {
  }

  ngOnInit(): void {
    this.drops$ = this.waxService.loadDrops();
    this.liveDrops$ = this.drops$.pipe(map((drops: TableRowDrop[]) => {
      const now = new Date().getTime() / 1000;
      const liveDrops = drops.filter((drop: TableRowDrop) => drop.start_time < now);
      liveDrops.map(drop => {
        this.assets$[drop.collection_name + drop.assets_to_mint[0].template_id] =
          this.waxService.searchAsset(drop.collection_name, drop.assets_to_mint[0].template_id);
      });
      return liveDrops;
    }));
    this.upcomingDrops$ = this.drops$.pipe(map((drops: TableRowDrop[]) => {
      const now = new Date().getTime() / 1000;
      const upcomingDrops = drops.filter((drop: TableRowDrop) => drop.start_time > now);
      upcomingDrops.map(drop => {
        this.assets$[drop.collection_name + drop.assets_to_mint[0].template_id] =
          this.waxService.searchAsset(drop.collection_name, drop.assets_to_mint[0].template_id);
      });
      return upcomingDrops;
    }));
    this.waxPrice$ = interval(15000).pipe(startWith(0), flatMap(() => {
      return this.waxService.getWaxPrice();
    }));
  }

  login(): void {
    if (!this.waxService.userAccount) {
      this.waxService.login().subscribe((result: string) => {
        console.log('logged in');
      }, (error: Error) => {
        if (error.message.includes('popup')) {
          alert('Failed to open the log-in pop-up ! Please unblock pop-ups for this domain and try again.');
        }
      });
    }
  }

  dropOpen(event: CountdownEvent, drop: TableRowDrop, waxPrice: number, quantity: string | number, autoclaim: boolean): void {
    if (event.action === 'done' && autoclaim) {
      this.claimDrop(drop, waxPrice, quantity);
    }
  }

  claimDrop(drop: TableRowDrop, waxPrice: number, quantity: number | string = 1): void {
    const startTime = new Date().getTime();
    if (typeof quantity === 'string') {
      quantity = parseInt(quantity, 10);
    }
    const actions = [];
    let currency = drop.listing_price.split(' ')[1];
    let price: number = parseFloat(drop.listing_price.split(' ')[0]);
    if (currency !== 'NULL') {
      if (currency === 'USD') {
        price = price / waxPrice;
        currency = 'WAX';
      }
      actions.push({
        account: currencyContracts[currency],
        name: 'transfer',
        authorization: [{
          actor: this.waxService.userAccount,
          permission: 'active',
        }],
        data: {
          from: this.waxService.userAccount,
          to: 'atomicdropsx',
          quantity: (price * quantity).toFixed(currencyPrecisions[currency]) + currency,
          memo: 'deposit',
        },
      });
    }
    from(this.waxService.wax.api.transact({
      actions: [
        ...actions,
        {
        account: 'atomicdropsx',
        name: 'claimdrop',
        authorization: [{
          actor: this.waxService.userAccount,
          permission: 'active',
        }],
        data: {
          claimer: this.waxService.userAccount,
          claim_amount: quantity,
          drop_id: drop.drop_id,
          intended_delphi_median: 0,
          referrer: 'atomichub',
        },
      }]
    }, {
      blocksBehind: 3,
      expireSeconds: 1200,
    })).subscribe(result => {
      this.snackBar.open('Transaction successful !', '', {
        duration: 5000,
      });
    }, (error) => {
      const endTime = new Date().getTime();
      console.log(`Failed after ${endTime - startTime}ms`);
      console.log('err', error);
      this.snackBar.open(error.message, '', {
        duration: 5000,
      });
    });
  }

  image(assetImage: string): string {
    if (assetImage.startsWith('http')) {
      return assetImage;
    } else {
      return 'https://wax.atomichub.io/ipfs/' + assetImage;
    }
  }

  price(price: string): string {
    if (price === '0 NULL') {
      return 'FREE';
    } else {
      return price;
    }
  }

  countdownConfig(drop: TableRowDrop, live: boolean): CountdownConfig {
    return {
      stopTime: live ? drop.end_time * 1000 : (drop.start_time * 1000) - 1000 // Try to preshot drop 1 second ahead
    };
  }

  loadAsset(drop: TableRowDrop): Observable<ImmutableData> {
    const assetsToMine = drop.assets_to_mint;
    return this.waxService.searchAsset(drop.collection_name, assetsToMine[0].template_id);
  }
}
